import React, { useRef, useState } from "react";
import SwiperCore, { Navigation, Pagination, Autoplay } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.min.css";
import styled, { css } from "styled-components";
import Section from "./Section";
import Container from "./Container";
import ArrowLeftIcon from "./Icons/ArrowLeft";
import ArrowRightIcon from "./Icons/ArrowRight";
import SafeSiteImage from "./SafeSiteImage";

const CarouselStyles = styled(Swiper)`
  ${({ ...props }) => css`
    .swiper-wrapper {
      aspect-ratio: 16/9
    }
    @media (min-width: ${props.theme.breakpoints.md}) {
      .swiper-wrapper {
        aspect-ratio: initial;
        height: 70vh
      }
    }

    .swiper-button-prev,
    .swiper-button-next {
      display: none;
    }
    .swiper-slide {
      height: auto;

      img {
        height: 100%;
        width: 100%;
        object-fit: cover;
        border-radius: ${props.$fullWidth ? '0' : '4px'}
      }

      > a {
        height: 100%;

        @media (min-width: ${props.theme.breakpoints.lg}) {
          display: flex;
        }
      }
    }

    .swiper-pagination-bullet {
      width: 14px;
      height: 14px;
      background: white;
      opacity: 1;
      border: 1px solid #707070;
      &.swiper-pagination-bullet-active {
        background: ${props.theme.colors.primary};
        border: 2px solid white;
      }
    }
  `}
`;

const CarouselWrapperStyles = styled.div`
  ${({ ...props }) => css`
    @media (max-width: 577px) {
      display: contents;
      > section {
        display: contents;
        > div {
          display: contents
        }
      }
    }
  `}
`;

const NavigationStyles = styled.div`
  ${({ ...props }) => css`
    display: ${(props.$hidden ? "none" : "flex")};
  `}
`;

const StyledButton = styled.button`
  ${({ ...props }) => css`
    background: #e8e8e8;
    line-height: 32px;
    padding: 0;
    border-radius: 50%;
    transition: all ease 0.3s;
    height: 32px;
    width: 32px;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 24px;
    z-index: 9;

    &.next {
      right: 16px;
    }

    &.prev {
      left: 16px;
    }

    &:hover {
      background: #dedede;
    }

    &.swiper-button-disabled {
      opacity: 0.5;
      cursor: default;

      &:hover {
        background: none;
      }
    }
  `}
`;

SwiperCore.use([Navigation, Pagination, Autoplay]);

export const Carousel = ({ images, fullWidth = true, autoScroll = true, delay = 3000 }) => {
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const [autoplay, setAutoplay] = useState(autoScroll);

  if (!Array.isArray(images)) {
    return <></>;
  }

  // filter out items in the array that do not have images => image.mediaItemUrl
  images = images.filter(image => image.mediaItemUrl);

  if (images.length === 0) {
    return <></>;
  }

  const Carousel = <CarouselStyles
    slidesPerView={1}
    navigation
    pagination={{ clickable: true }}
    wrapperTag="ul"
    onInit={(swiper) => {
      swiper.params.navigation.prevEl = prevRef.current;
      swiper.params.navigation.nextEl = nextRef.current;
      swiper.navigation.init();
      swiper.navigation.update();
    }}
    $fullWidth={fullWidth}
    loop
    autoplay={autoplay ? { delay, disableOnInteraction: true } : false}
    speed={500}
    onSlideChange={() => setAutoplay(false)}
  >
    {images.map((image, index) => (
      <SwiperSlide key={image.mediaItemUrl + index} tag="li">
        <SafeSiteImage
          image={image.mediaItemUrl}
          alt={image.altText}
        />
      </SwiperSlide>
    ))}
    <NavigationStyles
      $hidden={images.length === 1}
    >
      <StyledButton ref={prevRef} type="button" className="prev">
        <span className="sr-only">Prev</span>
        <ArrowLeftIcon />
      </StyledButton>
      <StyledButton ref={nextRef} type="button" className="next">
        <span className="sr-only">Next</span>
        <ArrowRightIcon />
      </StyledButton>
    </NavigationStyles>
  </CarouselStyles>

  if (fullWidth) {
    return Carousel;
  }

  return (
    <CarouselWrapperStyles>
      <Section>
        <Container>
          {Carousel}
        </Container>
      </Section>
    </CarouselWrapperStyles>
  )
};
